import type { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import { INTL_DAY_LONG_MONTH_YEAR, INTL_DAY_ONLY } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { RETURN_FOR_FREE_SALE_END_DATE, RETURN_FOR_FREE_SALE_START_DATE } from 'constants/charterFlight'
import MaldivesReturnForFreeMemberLabel from 'components/MaldivesDirect/MaldivesReturnForFreeMemberLabel'

const BorderedPanel = styled.div`
  border: 1px solid ${props => props.theme.palette.product.luxPlus.foreground};
  border-radius: ${rem(12)};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)} ${rem(28)};
  }
`

const emphasis = (chunks: ReactNode) => <em>{chunks}</em>

interface Props {
  onCheckAvailability: () => void
}

function MaldivesReturnForFreeBanner({ onCheckAvailability }: Props) {
  const intl = useIntl()

  const checkAvailabilityLabel = intl.formatMessage({
    id: 'UCJrtXxaIbwK1VwtP_fe',
    defaultMessage: 'Check availability',
    description: 'Call to action on the Maldives return for free campaign banner that opens the trip date selection calendar',
  })

  return (
    <Group direction="vertical" gap={20}>
      <BorderedPanel>
        <Group
          direction="vertical"
          tabletDirection="horizontal"
          gap={16}
          tabletGap={24}
          horizontalAlign="center"
          tabletHorizontalAlign="start"
          verticalAlign="center"
        >
          <MaldivesReturnForFreeMemberLabel />

          <Group direction="vertical" gap={2} fullWidth>
            <Heading variant="heading3" align="center" tabletAlign="start" colour="lux-plus">
              <FormattedMessage
                id="-NpG50PJshpzTEG8xjob"
                defaultMessage="Maldives return for <em>free</em> flight sale"
                description="Headline on the Maldives return for free campaign banner, with the word free emphasised in italics"
                values={{ em: emphasis }}
              />
            </Heading>
            <BodyText variant="medium" align="center" tabletAlign="start">
              {intl.formatMessage({
                id: 'ryHBsR5l4frAlCJhiIRr',
                defaultMessage: 'Book an eligible Maldives hotel and flight package and LuxPlus+ members can get the return flight for free. Limited seats available. Discount automatically applied.',
                description: 'Explanation of the Maldives return for free campaign eligibility on the charter landing page banner',
              })}
            </BodyText>
          </Group>

          <TextButton kind="tertiary" size="medium" onClick={onCheckAvailability}>
            {checkAvailabilityLabel}
          </TextButton>
        </Group>
      </BorderedPanel>

      <BodyText variant="small" colour="light-foreground-alt">
        <FormattedMessage
          id="mjXyS1Y16eqR70UPHNeh"
          defaultMessage="Valid through {startDate} to {endDate}, or until sold out. Membership required. Flights are non-refundable and not combinable with other offers. <link>T&Cs apply.</link>"
          description="Fine print under the Maldives return for free campaign banner covering the sale window and flight conditions"
          values={{
            startDate: intlDateFormatter(RETURN_FOR_FREE_SALE_START_DATE, INTL_DAY_ONLY, intl.locale),
            endDate: intlDateFormatter(RETURN_FOR_FREE_SALE_END_DATE, INTL_DAY_LONG_MONTH_YEAR, intl.locale),
            link: (chunks) => (
              <TextLink to="/promotions" weight="regular">
                {chunks}
              </TextLink>
            ),
          }}
        />
      </BodyText>
    </Group>
  )
}

export default MaldivesReturnForFreeBanner
